/**
 * OpenAPI 3.1 document generator.
 *
 * `buildOpenApiDocument()` composes all route-group schemas into a single
 * registry and renders an OpenAPI 3.1 document. The document is generated at
 * build time by `scripts/build-openapi.mjs` and emitted to
 * `dist/openapi.json` (referenced by Tech Writer and frontend tooling).
 *
 * Workers-and-browser-safe: this module only depends on Zod and
 * `@asteasolutions/zod-to-openapi`, both of which are pure ESM. No `fs`, no
 * `path`, no `node:*`. The actual disk write happens in the build script,
 * which is an explicitly Node-only entry point and never bundled into the
 * frontend.
 */
import { extendZodWithOpenApi, OpenAPIRegistry, OpenApiGeneratorV31, } from "@asteasolutions/zod-to-openapi";
import { z } from "zod";
import { AgentDetailResponseSchema, AgentResponseSchema, AgentVersionResponseSchema, CreateAgentRequestSchema, CreateAgentResponseSchema, UpdateAgentRequestSchema, UpdateAgentResponseSchema, } from "./agents.js";
import { paginatedResponse } from "./common.js";
import { ErrorEnvelopeSchema } from "./errors.js";
import { ListRunsQuerySchema, ListRunsResponseSchema, RunResponseSchema, TriggerRunRequestSchema, TriggerRunResponseSchema, } from "./runs.js";
import { SecretListItemResponseSchema, SecretListResponseSchema, SecretNameSchema, SetSecretRequestSchema, } from "./secrets.js";
import { MemberResponseSchema, MeResponseSchema, TenantResponseSchema, UpdateTenantRequestSchema, } from "./tenants.js";
import { ExecutorCallbackPayloadSchema, StripeWebhookEventSchema } from "./webhooks.js";
// Augment Zod with `.openapi()` method. Idempotent. Safe to call from any
// context that imports this module.
extendZodWithOpenApi(z);
/**
 * Builds and returns an OpenAPI 3.1 document covering all Phase-1 endpoints.
 *
 * Pure function — no I/O, no module-load side effects beyond
 * `extendZodWithOpenApi(z)` which the library guarantees is idempotent.
 */
export function buildOpenApiDocument() {
    const registry = new OpenAPIRegistry();
    // ─── Reusable component schemas ──────────────────────────────────────────
    const ErrorEnvelopeRef = registry.register("ErrorEnvelope", ErrorEnvelopeSchema);
    const errorResponse = (description) => ({
        description,
        content: { "application/json": { schema: ErrorEnvelopeRef } },
    });
    registry.register("MeResponse", MeResponseSchema);
    registry.register("Tenant", TenantResponseSchema);
    registry.register("UpdateTenantRequest", UpdateTenantRequestSchema);
    registry.register("Member", MemberResponseSchema);
    registry.register("Agent", AgentResponseSchema);
    registry.register("AgentDetail", AgentDetailResponseSchema);
    registry.register("AgentVersion", AgentVersionResponseSchema);
    registry.register("CreateAgentRequest", CreateAgentRequestSchema);
    registry.register("CreateAgentResponse", CreateAgentResponseSchema);
    registry.register("UpdateAgentRequest", UpdateAgentRequestSchema);
    registry.register("UpdateAgentResponse", UpdateAgentResponseSchema);
    registry.register("Run", RunResponseSchema);
    registry.register("TriggerRunRequest", TriggerRunRequestSchema);
    registry.register("TriggerRunResponse", TriggerRunResponseSchema);
    registry.register("ListRunsResponse", ListRunsResponseSchema);
    registry.register("Secret", SecretListItemResponseSchema);
    registry.register("SetSecretRequest", SetSecretRequestSchema);
    registry.register("SecretListResponse", SecretListResponseSchema);
    registry.register("StripeWebhookEvent", StripeWebhookEventSchema);
    registry.register("ExecutorCallbackPayload", ExecutorCallbackPayloadSchema);
    // ─── Tenants / me ────────────────────────────────────────────────────────
    registry.registerPath({
        method: "get",
        path: "/api/v1/me",
        tags: ["tenants"],
        summary: "Get the authenticated user and active tenant",
        responses: {
            200: {
                description: "Current user, active tenant, and role",
                content: { "application/json": { schema: MeResponseSchema } },
            },
            401: errorResponse("Unauthorized"),
        },
    });
    registry.registerPath({
        method: "get",
        path: "/api/v1/tenants/current",
        tags: ["tenants"],
        summary: "Get the active tenant",
        responses: {
            200: {
                description: "Active tenant",
                content: { "application/json": { schema: TenantResponseSchema } },
            },
            401: errorResponse("Unauthorized"),
        },
    });
    registry.registerPath({
        method: "patch",
        path: "/api/v1/tenants/current",
        tags: ["tenants"],
        summary: "Update the active tenant",
        request: {
            body: {
                required: true,
                content: { "application/json": { schema: UpdateTenantRequestSchema } },
            },
        },
        responses: {
            200: {
                description: "Updated tenant",
                content: { "application/json": { schema: TenantResponseSchema } },
            },
            400: errorResponse("Invalid input"),
            401: errorResponse("Unauthorized"),
        },
    });
    registry.registerPath({
        method: "get",
        path: "/api/v1/tenants/current/members",
        tags: ["tenants"],
        summary: "List members of the active tenant",
        responses: {
            200: {
                description: "List of tenant members",
                content: {
                    "application/json": { schema: paginatedResponse(MemberResponseSchema) },
                },
            },
            401: errorResponse("Unauthorized"),
        },
    });
    // ─── Agents ──────────────────────────────────────────────────────────────
    registry.registerPath({
        method: "post",
        path: "/api/v1/agents",
        tags: ["agents"],
        summary: "Create a new agent (creates v1)",
        request: {
            body: {
                required: true,
                content: { "application/json": { schema: CreateAgentRequestSchema } },
            },
        },
        responses: {
            201: {
                description: "Created agent (compact response)",
                content: { "application/json": { schema: CreateAgentResponseSchema } },
            },
            400: errorResponse("Invalid input"),
            401: errorResponse("Unauthorized"),
            403: errorResponse("Role not authorized"),
        },
    });
    registry.registerPath({
        method: "get",
        path: "/api/v1/agents",
        tags: ["agents"],
        summary: "List agents in the active tenant",
        responses: {
            200: {
                description: "Paginated list of agents",
                content: {
                    "application/json": { schema: paginatedResponse(AgentResponseSchema) },
                },
            },
            400: errorResponse("Invalid limit query parameter"),
            401: errorResponse("Unauthorized"),
        },
    });
    registry.registerPath({
        method: "get",
        path: "/api/v1/agents/{id}",
        tags: ["agents"],
        summary: "Get an agent's detail including current_version_config",
        request: { params: z.object({ id: z.string().uuid() }) },
        responses: {
            200: {
                description: "Agent detail with current version config",
                content: { "application/json": { schema: AgentDetailResponseSchema } },
            },
            401: errorResponse("Unauthorized"),
            404: errorResponse("Agent not found (or cross-tenant)"),
        },
    });
    registry.registerPath({
        method: "patch",
        path: "/api/v1/agents/{id}",
        tags: ["agents"],
        summary: "Update an agent (creates a new version)",
        request: {
            params: z.object({ id: z.string().uuid() }),
            body: {
                required: true,
                content: { "application/json": { schema: UpdateAgentRequestSchema } },
            },
        },
        responses: {
            200: {
                description: "Newly-created agent version (compact response)",
                content: { "application/json": { schema: UpdateAgentResponseSchema } },
            },
            400: errorResponse("Invalid input"),
            401: errorResponse("Unauthorized"),
            403: errorResponse("Role not authorized"),
            404: errorResponse("Agent not found (or cross-tenant)"),
        },
    });
    registry.registerPath({
        method: "get",
        path: "/api/v1/agents/{id}/versions",
        tags: ["agents"],
        summary: "List version history for an agent",
        request: { params: z.object({ id: z.string().uuid() }) },
        responses: {
            200: {
                description: "Paginated agent versions",
                content: {
                    "application/json": { schema: paginatedResponse(AgentVersionResponseSchema) },
                },
            },
            401: errorResponse("Unauthorized"),
            404: errorResponse("Agent not found"),
        },
    });
    // ─── Runs ────────────────────────────────────────────────────────────────
    registry.registerPath({
        method: "post",
        path: "/api/v1/agents/{id}/runs",
        tags: ["runs"],
        summary: "Trigger a run of an agent",
        request: {
            params: z.object({ id: z.string().uuid() }),
            body: {
                required: true,
                content: { "application/json": { schema: TriggerRunRequestSchema } },
            },
        },
        responses: {
            202: {
                description: "Run accepted; processing asynchronously",
                content: { "application/json": { schema: TriggerRunResponseSchema } },
            },
            400: errorResponse("Invalid input"),
            401: errorResponse("Unauthorized"),
            404: errorResponse("Agent not found"),
            429: errorResponse("Rate limited or budget exceeded"),
        },
    });
    registry.registerPath({
        method: "get",
        path: "/api/v1/runs",
        tags: ["runs"],
        summary: "List runs across agents",
        request: { query: ListRunsQuerySchema },
        responses: {
            200: {
                description: "Paginated runs",
                content: { "application/json": { schema: ListRunsResponseSchema } },
            },
            401: errorResponse("Unauthorized"),
        },
    });
    registry.registerPath({
        method: "get",
        path: "/api/v1/runs/{id}",
        tags: ["runs"],
        summary: "Get a run by id",
        request: { params: z.object({ id: z.string().uuid() }) },
        responses: {
            200: {
                description: "Run detail",
                content: { "application/json": { schema: RunResponseSchema } },
            },
            401: errorResponse("Unauthorized"),
            404: errorResponse("Run not found"),
        },
    });
    registry.registerPath({
        method: "post",
        path: "/api/v1/runs/{id}/cancel",
        tags: ["runs"],
        summary: "Cancel an in-flight run",
        request: { params: z.object({ id: z.string().uuid() }) },
        responses: {
            204: { description: "Cancellation requested" },
            401: errorResponse("Unauthorized"),
            404: errorResponse("Run not found"),
            409: errorResponse("Run already completed"),
        },
    });
    // ─── Secrets ─────────────────────────────────────────────────────────────
    registry.registerPath({
        method: "get",
        path: "/api/v1/secrets",
        tags: ["secrets"],
        summary: "List secret names for the active tenant (NEVER returns plaintext)",
        responses: {
            200: {
                description: "Secret names + metadata; values are server-only",
                content: { "application/json": { schema: SecretListResponseSchema } },
            },
            401: errorResponse("Unauthorized"),
        },
    });
    registry.registerPath({
        method: "put",
        path: "/api/v1/secrets/{name}",
        tags: ["secrets"],
        summary: "Set a secret value (encrypted at rest)",
        request: {
            params: z.object({ name: SecretNameSchema }),
            body: {
                required: true,
                content: { "application/json": { schema: SetSecretRequestSchema } },
            },
        },
        responses: {
            200: {
                description: "Secret created or rotated; response carries name only",
                content: { "application/json": { schema: SecretListItemResponseSchema } },
            },
            400: errorResponse("Invalid input"),
            401: errorResponse("Unauthorized"),
        },
    });
    registry.registerPath({
        method: "delete",
        path: "/api/v1/secrets/{name}",
        tags: ["secrets"],
        summary: "Delete a secret",
        request: { params: z.object({ name: SecretNameSchema }) },
        responses: {
            204: { description: "Secret deleted" },
            401: errorResponse("Unauthorized"),
            404: errorResponse("Secret not found"),
        },
    });
    // ─── Webhooks ────────────────────────────────────────────────────────────
    registry.registerPath({
        method: "post",
        path: "/api/v1/webhooks/stripe",
        tags: ["webhooks"],
        summary: "Stripe webhook event receiver",
        request: {
            body: {
                required: true,
                content: { "application/json": { schema: StripeWebhookEventSchema } },
            },
        },
        responses: {
            200: { description: "Event accepted" },
            400: errorResponse("Invalid signature or payload"),
        },
    });
    registry.registerPath({
        method: "post",
        path: "/internal/runs/{id}/callback",
        tags: ["webhooks"],
        summary: "Executor → orchestrator run callback (HMAC-authenticated)",
        request: {
            params: z.object({ id: z.string().uuid() }),
            body: {
                required: true,
                content: { "application/json": { schema: ExecutorCallbackPayloadSchema } },
            },
        },
        responses: {
            200: { description: "Callback accepted" },
            401: errorResponse("Invalid HMAC token"),
        },
    });
    const generator = new OpenApiGeneratorV31(registry.definitions);
    return generator.generateDocument({
        openapi: "3.1.0",
        info: {
            title: "Simpliwork Relay Portal API",
            version: "1.0.0",
            description: "Phase 1 endpoints generated from @simpliwork/api-schema Zod schemas.",
        },
        servers: [{ url: "https://api.simpliwork.bot", description: "Production" }],
    });
}
//# sourceMappingURL=openapi.js.map